import {
  BROKER_SYNC_PROVIDER,
  BROKER_SYNC_PROVIDER_LABELS,
  type TBrokerSyncProvider,
} from "@signals/shared";
import { useEffect, useState, type FC, type FormEvent } from "react";
import { translate, type TranslationKey } from "../../locales";
import { Button, Modal, Select } from "../../_common";
import { useUpdateAccount, type Account } from "../portfolio.hooks";
import { apiErrorMessage } from "../portfolio-api-error";

interface EditBrokerSyncAccountModalProps {
  open: boolean;
  account: Account | null;
  onClose: () => void;
}

/** Same language as the "Счета" tab and the broker sync panel: one language per screen. */
const MODAL_LOCALE = "ru-RU";

function t(key: TranslationKey): string {
  return translate(MODAL_LOCALE, key);
}

const inputClass = "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const labelClass = "text-sm font-medium";

export const EditBrokerSyncAccountModal: FC<EditBrokerSyncAccountModalProps> = ({
  open,
  account,
  onClose,
}) => {
  const updateAccount = useUpdateAccount();
  const [brokerAccountId, setBrokerAccountId] = useState("");
  const [provider, setProvider] = useState<TBrokerSyncProvider | "">("");
  const [from, setFrom] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !account) return;
    setBrokerAccountId(account.brokerAccountId ?? "");
    setProvider(account.brokerSyncProvider ?? "");
    setFrom(account.brokerSyncFrom ?? "");
    setError(null);
  }, [open, account]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!account) return;
    if (provider && (!brokerAccountId.trim() || !from)) {
      setError(t("portfolio.brokerSync.linkRequired"));
      return;
    }
    setError(null);
    try {
      await updateAccount.mutateAsync({
        id: account.id,
        body: {
          brokerAccountId: brokerAccountId.trim(),
          brokerSyncProvider: provider || null,
          brokerSyncFrom: provider ? from : null,
        },
      });
      onClose();
    } catch (caught) {
      // The server message names the conflicting account ("already linked to account ...").
      setError(apiErrorMessage(caught, t("portfolio.brokerSync.linkFailed")));
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("portfolio.brokerSync.linkTitle")}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label className={labelClass} htmlFor="edit-broker-account-id">{t("portfolio.brokerSync.brokerAccountId")}</label>
        <input
          id="edit-broker-account-id"
          className={inputClass}
          value={brokerAccountId}
          onChange={(event) => setBrokerAccountId(event.target.value)}
          aria-label={t("portfolio.brokerSync.brokerAccountId")}
        />

        <label className={labelClass}>{t("portfolio.brokerSync.linkProvider")}</label>
        <Select
          className={inputClass}
          value={provider}
          onChange={(value) => {
            const selected = BROKER_SYNC_PROVIDER.find((candidate) => candidate === value);
            setProvider(selected ?? "");
          }}
          placeholder={t("portfolio.brokerSync.linkProviderDisabled")}
          options={[
            { value: "", label: t("portfolio.brokerSync.linkProviderDisabled") },
            ...BROKER_SYNC_PROVIDER.map((item) => ({
              value: item,
              label: BROKER_SYNC_PROVIDER_LABELS[item][MODAL_LOCALE] ?? item,
            })),
          ]}
          ariaLabel={t("portfolio.brokerSync.linkProvider")}
        />

        {provider && (
          <>
            <label className={labelClass} htmlFor="edit-broker-sync-from">{t("portfolio.brokerSync.linkFrom")}</label>
            <input
              id="edit-broker-sync-from"
              className={inputClass}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              aria-label={t("portfolio.brokerSync.linkFrom")}
            />
          </>
        )}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>{t("portfolio.brokerSync.cancel")}</Button>
          <Button type="submit" disabled={updateAccount.isPending || !account}>
            {updateAccount.isPending ? t("portfolio.brokerSync.saving") : t("portfolio.brokerSync.saveLink")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
